import { apiClient, ApiResponse } from './api';
import { CV_PARSE_FILE_TIMEOUT_MS, CV_PARSE_TEXT_TIMEOUT_MS, ATS_ANALYSIS_TIMEOUT_MS } from '@/config/timeouts';
import { useAuthStore } from '@/store/auth.store';
import { useProfileStore } from '@/store/profile.store';
import { localCVService } from './local/cv.local';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';
// Vite-friendly worker URL for pdfjs (ESM build)
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import mammoth from 'mammoth/mammoth.browser';
import type { ATSAnalysisResult } from '@shared/types/cv';
import type { FullATSScoreV3 } from '@shared/types/ats-report';
import { v2ToV3 } from '@shared/types/ats-report-adapters';

export interface ParsedCV {
  personalInfo: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    location?: string;
    fullName?: string;
    linkedin?: string;
    website?: string;
  };
  summary?: string;
  experience: Array<{
    company: string;
    position: string;
    startDate: string;
    endDate?: string;
    isCurrent?: boolean;
    description?: string;
    achievements?: string[];
  }>;
  education: Array<{
    institution: string;
    degree: string;
    field?: string;
    startDate: string;
    endDate?: string;
    gpa?: string;
    achievements?: string[];
  }>;
  skills: string[];
  languages?: Array<{
    language: string;
    level: string;
  }>;
  certifications?: Array<{
    name: string;
    issuer?: string;
    date?: string;
    expirationDate?: string;
  }>;
}

const EMAIL_PATTERN = /[\w.-]+@[\w.-]+\.\w+/i;
const EMAIL_PATTERN_GLOBAL = /[\w.-]+@[\w.-]+\.\w+/gi;
const PHONE_PATTERN = /\+?[0-9()[\]\s.+-]{7,}/gi;
const PHONE_TEST_PATTERN = /\+?[0-9()[\]\s.+-]{7,}/i;
const PDF_WORKER_SRC = pdfWorker;
const LINKEDIN_PATTERN = /https?:\/\/(?:www\.)?linkedin\.com\/[^\s]+/i;
const LOCATION_HINTS = [
  'kingdom',
  'states',
  'united',
  'city',
  'london',
  'bristol',
  'york',
  'germany',
  'france',
  'italy',
  'spain',
  'poland',
  'ukraine',
  'russia',
  'canada',
  'australia',
  'emirates',
  'uae',
];
const SUMMARY_STOP_KEYWORDS = [
  'skills',
  'experience',
  'work experience',
  'education',
  'projects',
  'certifications',
  'languages',
];
const SECTION_STOP_WORDS = [
  'professional experience',
  'experience',
  'work experience',
  'employment',
  'skills',
  'education',
  'projects',
  'certifications',
  'languages',
];

if (GlobalWorkerOptions.workerSrc !== PDF_WORKER_SRC) {
  GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
}

/**
 * Extract raw text from PDF/DOCX/TXT on the client for unauthenticated users
 */
async function extractTextFromFile(file: File): Promise<string> {
  const extension = file.name.toLowerCase().split('.').pop();

  try {
    // PDF parsing via pdfjs-dist
    if (file.type === 'application/pdf' || extension === 'pdf') {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await getDocument({ data: arrayBuffer }).promise;
      const pages: string[] = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items
          .map((item) => ('str' in item ? (item as TextItem).str : ''))
          .join(' ');
        pages.push(pageText);
      }

      return pages.join('\n');
    }

    // DOCX parsing via mammoth
    if (
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      extension === 'docx'
    ) {
      const arrayBuffer = await file.arrayBuffer();
      const { value } = await mammoth.extractRawText({ arrayBuffer });
      return value;
    }

    // Plain text fallback for TXT/unknown formats
    const raw = await file.text();
    return cleanPdfArtifacts(raw);
  } catch (error) {
    console.warn('Local text extraction failed, falling back to plain text', error);
    const raw = await file.text();
    return cleanPdfArtifacts(raw);
  }
}

function cleanPdfArtifacts(text: string): string {
  return text
    .replace(/\u0000/g, ' ')
    .replace(/%PDF-\d\.\d/gi, ' ')
    .replace(/\b\d+\s+0\s+obj\b/gi, ' ')
    .replace(/\bendobj\b/gi, ' ')
    .replace(/\bstream\b|\bendstream\b/gi, ' ')
    .replace(/[^\x09\x0A\x0D\x20-\x7E]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function normalizeLines(text: string): string[] {
  return cleanPdfArtifacts(text)
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function extractSection(text: string, header: RegExp, stopWords: RegExp[]): string | null {
  const lower = text.toLowerCase();
  const headerMatch = lower.match(header);
  if (headerMatch?.index === undefined) return null;
  const start = headerMatch.index + headerMatch[0].length;
  const tail = text.slice(start);

  let end = tail.length;
  for (const stop of stopWords) {
    const m = tail.toLowerCase().match(stop);
    if (m?.index !== undefined && m.index < end) {
      end = m.index;
    }
  }

  const section = tail.slice(0, end).trim();
  return section.length > 0 ? section : null;
}

function mergeParsedCV(primary: ParsedCV, secondary: ParsedCV): ParsedCV {
  const safePrimary = {
    personalInfo: primary?.personalInfo || ({} as ParsedCV['personalInfo']),
    summary: primary?.summary,
    experience: primary?.experience || [],
    education: primary?.education || [],
    skills: primary?.skills || [],
    languages: primary?.languages || [],
  };
  const safeSecondary = {
    personalInfo: secondary?.personalInfo || ({} as ParsedCV['personalInfo']),
    summary: secondary?.summary,
    experience: secondary?.experience || [],
    education: secondary?.education || [],
    skills: secondary?.skills || [],
    languages: secondary?.languages || [],
  };

  const pickLonger = (a?: string, b?: string) => ((a || '').length >= (b || '').length ? a : b);

  return {
    personalInfo: {
      firstName: safePrimary.personalInfo.firstName || safeSecondary.personalInfo.firstName || '',
      lastName: safePrimary.personalInfo.lastName || safeSecondary.personalInfo.lastName || '',
      email: safePrimary.personalInfo.email || safeSecondary.personalInfo.email,
      phone: safePrimary.personalInfo.phone || safeSecondary.personalInfo.phone,
      location: safePrimary.personalInfo.location || safeSecondary.personalInfo.location,
    },
    summary: pickLonger(safePrimary.summary, safeSecondary.summary),
    experience: safePrimary.experience.length ? safePrimary.experience : safeSecondary.experience,
    education: safePrimary.education.length ? safePrimary.education : safeSecondary.education,
    skills: safePrimary.skills.length ? safePrimary.skills : safeSecondary.skills,
    languages: safePrimary.languages?.length ? safePrimary.languages : safeSecondary.languages,
  };
}

function extractParsedCV(raw: any): ParsedCV {
  // Backend returns { data: ParsedCV, validation, source }
  // If already ParsedCV shape, normalize as-is
  const payload = raw?.data ? raw.data : raw;
  return normalizeServerParsed(payload);
}

function normalizeServerParsed(data: any): ParsedCV {
  const personal = data?.personalInfo || {};
  const fullName: string = personal.fullName || '';
  const parts = fullName.trim().split(/\s+/);
  const firstName = personal.firstName || parts[0] || '';
  const lastName = personal.lastName || parts.slice(1).join(' ') || '';

  const skillsCombined: string[] = [];
  if (Array.isArray(data?.skills)) {
    skillsCombined.push(...data.skills);
  } else if (data?.skills) {
    if (Array.isArray(data.skills.technical)) skillsCombined.push(...data.skills.technical);
    if (Array.isArray(data.skills.soft)) skillsCombined.push(...data.skills.soft);
    if (Array.isArray(data.skills.languages)) skillsCombined.push(...data.skills.languages);
  }

  const languagesArr: Array<{ language: string; level: string }> = [];
  if (Array.isArray(data?.skills?.languages)) {
    for (const lang of data.skills.languages) {
      if (!lang) continue;
      if (typeof lang === 'string') {
        languagesArr.push({ language: lang, level: 'unknown' });
      } else {
        languagesArr.push({
          language: lang.language || '',
          level: lang.level || 'unknown',
        });
      }
    }
  } else if (Array.isArray(data?.languages)) {
    for (const lang of data.languages) {
      if (!lang) continue;
      languagesArr.push({
        language: lang.language || lang.name || '',
        level: lang.level || 'unknown',
      });
    }
  }

  const experience = Array.isArray(data?.experience)
    ? data.experience.map((exp: any) => ({
        company: exp.company || '',
        position: exp.position || '',
        startDate: exp.startDate || '',
        endDate: exp.isCurrent ? '' : exp.endDate || '',
        isCurrent: Boolean(exp.isCurrent) || (!exp.endDate && exp.startDate ? false : false),
        // Combine description + achievements into one text block to avoid losing bullet points
        description: [
          exp.description || '',
          ...(Array.isArray(exp.achievements) ? exp.achievements : []),
        ]
          .filter(Boolean)
          .join('\n'),
        achievements: Array.isArray(exp.achievements) ? exp.achievements : [],
      }))
    : [];

  const education = Array.isArray(data?.education)
    ? data.education.map((edu: any) => ({
        institution: edu.institution || '',
        degree: edu.degree || '',
        field: edu.field || '',
        startDate: edu.startDate || '',
        endDate: edu.endDate || '',
        gpa: edu.gpa ? String(edu.gpa) : undefined,
        achievements: Array.isArray(edu.achievements) ? edu.achievements : [],
      }))
    : [];

  const certifications = Array.isArray(data?.certifications)
    ? data.certifications.map((cert: any) => ({
        name: cert.name || '',
        issuer: cert.issuer || '',
        date: cert.date || '',
        expirationDate: cert.expiryDate || cert.expirationDate || '',
      }))
    : [];

  return {
    personalInfo: {
      firstName,
      lastName,
      email: personal.email || '',
      phone: personal.phone || '',
      location: personal.location || '',
      fullName,
      linkedin: personal.linkedin,
      website: personal.website,
    },
    summary: data?.summary || '',
    experience,
    education,
    skills: skillsCombined,
    languages: languagesArr,
    certifications,
  };
}

function pickNameLine(lines: string[]): string {
  for (const line of lines.slice(0, 10)) {
    if (!EMAIL_PATTERN.test(line) && !PHONE_TEST_PATTERN.test(line) && line.length < 100) {
      return line;
    }
  }
  return '';
}

function extractSkillsFromText(text: string): string[] {
  const skillsMatch = text.match(/(?:skills|core competencies|technologies)[:\s]+([\s\S]{0,400})/i);
  const skillsSource = skillsMatch ? skillsMatch[1] : '';
  const rawTokens = skillsSource
    .replace(/[•●▪◦]/g, ' ')
    .split(/[\n;,|•●▪◦]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  return Array.from(new Set(rawTokens));
}

/**
 * Local CV parser - enhanced extraction without server
 * Used when user is not authenticated
 */
async function parseLocalCV(file: File): Promise<ParsedCV> {
  const text = await extractTextFromFile(file);
  const lines = normalizeLines(text);
  const cleaned = cleanPdfArtifacts(text);

  // Contacts
  const emailMatch = cleaned.match(EMAIL_PATTERN_GLOBAL);
  const phoneMatch = cleaned.match(PHONE_PATTERN);
  const linkedinMatch = cleaned.match(LINKEDIN_PATTERN);

  // Heuristic: find earliest contact index to delimit name/location prefix
  const contactIndices = [emailMatch?.index, phoneMatch?.index, linkedinMatch?.index].filter(
    (v) => typeof v === 'number'
  ) as number[];
  const cutoff = contactIndices.length > 0 ? Math.min(...contactIndices) : cleaned.length;
  const headerFragment = cleaned.slice(0, cutoff);

  // Name: try from header fragment; fallback to first non-contact line
  const headerTokens = headerFragment
    .split(/\s+/)
    .filter((t) => t && !EMAIL_PATTERN.test(t) && !PHONE_TEST_PATTERN.test(t))
    .slice(0, 6);
  const probableNameLine = headerTokens.join(' ').trim();
  const nameSource = probableNameLine || pickNameLine(lines);
  const nameParts = nameSource.split(/\s+/).filter(Boolean);
  const firstName = nameParts[0] || '';
  const lastName = nameParts[1] || '';

  // Location: try to capture "United Kingdom, Bristol" pattern near header
  let location: string | undefined;
  const locationMatch = headerFragment.match(
    /([A-Z][a-z]+(?: [A-Z][a-z]+)*(?:,\s*[A-Z][a-z]+(?: [A-Z][a-z]+)*)?)/
  );
  if (locationMatch) {
    const locCandidate = locationMatch[1];
    if (LOCATION_HINTS.some((hint) => locCandidate.toLowerCase().includes(hint))) {
      location = locCandidate.trim();
    }
  }

  // Summary: приоритет — явный раздел SUMMARY; fallback — текст после контактов до стоп-слов
  const summarySection =
    extractSection(
      cleaned,
      /\bsummary\b/,
      SECTION_STOP_WORDS.map((w) => new RegExp(`\\b${w}\\b`))
    ) ||
    extractSection(cleaned, /\bsummary\b/, [/\bprofessional experience\b/i, /\bexperience\b/i]);

  const linesAfterContacts = lines.filter(
    (line) =>
      !EMAIL_PATTERN.test(line) &&
      !PHONE_TEST_PATTERN.test(line) &&
      !line.toLowerCase().includes('linkedin.com')
  );
  const stopIndex = linesAfterContacts.findIndex((line) =>
    SUMMARY_STOP_KEYWORDS.some((kw) => line.toLowerCase().includes(kw))
  );
  const summarySource =
    stopIndex > 0 ? linesAfterContacts.slice(0, stopIndex) : linesAfterContacts.slice(0, 6);
  const summary = (summarySection || summarySource.join(' ')).trim();

  // Skills
  const skills = extractSkillsFromText(cleaned);

  return {
    personalInfo: {
      firstName,
      lastName,
      email: emailMatch?.[0],
      phone: phoneMatch?.[0]?.trim(),
      location,
    },
    summary,
    experience: [],
    education: [],
    skills,
  };
}

export type ExportFormat = 'pdf' | 'docx' | 'txt' | 'html';

export interface CVGenerationOptions {
  templateId?: string;
  template?: 'professional' | 'modern' | 'twoColumn' | 'harvard';
  includePhoto?: boolean;
  sections?: string[];
  customSummary?: string;
}

export interface GeneratedCV {
  id: string;
  userId: string;
  profileId?: string;
  vacancyId?: string;
  templateId: string;
  content: Record<string, unknown>;
  rendered: string;
  atsScore?: number;
  createdAt: Date;
}

export interface ExportResult {
  buffer: Blob;
  filename: string;
  mimeType: string;
}

export const cvService = {
  /**
   * Parse CV file (supports both local and server modes)
   */
  async parseCV(file: File): Promise<ParsedCV> {
    // Try server parsing first (even for unauthenticated users). Fallbacks:
    // 1) AI text parse and merge if server result is incomplete
    // 2) Local parse if server unavailable
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post<ApiResponse<ParsedCV>>(
        '/api/v1/cv/parse/file',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: CV_PARSE_FILE_TIMEOUT_MS,
        }
      );
      const serverRaw = response.data.data;
      const serverResult = extractParsedCV(serverRaw);
      console.log('[cvService.parseCV] server result (file)', {
        data: serverResult,
      });

      const needsAiEnhance =
        !serverResult.summary ||
        serverResult.experience.length === 0 ||
        serverResult.education.length === 0 ||
        serverResult.skills.length === 0;

      if (needsAiEnhance) {
        try {
          const rawText = await extractTextFromFile(file);
          const aiResponse = await apiClient.post<ApiResponse<ParsedCV>>(
            '/api/v1/cv/parse/text',
            {
              text: rawText,
            },
            {
              timeout: CV_PARSE_TEXT_TIMEOUT_MS,
            }
          );
          const aiResult = extractParsedCV(aiResponse.data.data);
          console.log('[cvService.parseCV] ai enhancement result (file->text)', {
            data: aiResult,
          });
          return mergeParsedCV(serverResult, aiResult);
        } catch (aiError) {
          console.warn('AI enhancement failed, using server result', aiError);
          return serverResult;
        }
      }

      return serverResult;
    } catch (error) {
      console.warn('Server CV parsing failed, trying AI text parsing:', error);

      // Retry server parsing once on timeout (ECONNABORTED) before AI text fallback
      const isTimeout = (error as any)?.code === 'ECONNABORTED';
      if (isTimeout) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const retryResponse = await apiClient.post<ApiResponse<ParsedCV>>(
            '/api/v1/cv/parse/file',
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: CV_PARSE_FILE_TIMEOUT_MS,
            }
          );
          const retryResult = extractParsedCV(retryResponse.data.data);
          console.log('[cvService.parseCV] retry server result (file)', {
            data: retryResult,
          });
          return retryResult;
        } catch (retryErr) {
          console.warn('Retry server parse failed, falling back to AI text:', retryErr);
        }
      }

      // Fallback chain: AI parse text -> local parse
      try {
        const rawText = await extractTextFromFile(file);
        const aiResponse = await apiClient.post<ApiResponse<ParsedCV>>(
          '/api/v1/cv/parse/text',
          {
            text: rawText,
          },
          {
            timeout: CV_PARSE_TEXT_TIMEOUT_MS,
          }
        );
        const aiResult = extractParsedCV(aiResponse.data.data);
        console.log('[cvService.parseCV] ai fallback result (file->text)', {
          data: aiResult,
        });
        return aiResult;
      } catch (aiError) {
        console.warn('AI text parsing failed, using local parsing:', aiError);
        return parseLocalCV(file);
      }
    }
  },

  /**
   * Parse CV from plain text (used when user pastes CV/LinkedIn content)
   */
  async parseText(text: string): Promise<ParsedCV> {
    const response = await apiClient.post<ApiResponse<ParsedCV>>(
      '/api/v1/cv/parse/text',
      { text },
      { timeout: CV_PARSE_TEXT_TIMEOUT_MS }
    );
    const parsed = extractParsedCV(response.data.data);
    console.log('[cvService.parseText] server result (text)', { data: parsed });
    return parsed;
  },

  /**
   * Get parsing progress (if async)
   */
  async getParsingStatus(jobId: string): Promise<{ status: string; result?: ParsedCV }> {
    const response = await apiClient.get<ApiResponse<{ status: string; result?: ParsedCV }>>(
      `/api/v1/cv/parse/status/${jobId}`
    );
    return response.data.data;
  },

  /**
   * Generate general-purpose CV from user's profile
   * ALWAYS calls server for AI generation, then saves to localStorage if not authenticated
   */
  async generateCV(
    options?: CVGenerationOptions
  ): Promise<{ cv: GeneratedCV; rendered: string; atsScore?: number }> {
    const { isAuthenticated } = useAuthStore.getState();

    try {
      // Build request body
      const requestBody: { options?: CVGenerationOptions; profileData?: any } = { options };

      // Always include profileData as fallback — backend prefers DB profile
      // for authenticated users, but uses this if DB profile is empty/missing
      const { profile } = useProfileStore.getState();
      if (!isAuthenticated && !profile) {
        throw new Error('Profile not found. Please create a profile first.');
      }
      if (profile) {
        requestBody.profileData = profile;
      }

      // ALWAYS call server for AI generation
      const response = await apiClient.post<
        ApiResponse<{ cv: GeneratedCV; rendered: string; atsScore?: number }>
      >('/api/v1/cv/generate', requestBody);

      const result = response.data.data;

      // Save to localStorage if not authenticated
      if (!isAuthenticated) {
        await localCVService.save(result.cv);
      }

      return result;
    } catch (error) {
      console.error('CV generation failed:', error);
      throw error;
    }
  },

  /**
   * Generate CV optimized for a specific vacancy
   * ALWAYS calls server for AI generation, then saves to localStorage if not authenticated
   */
  async generateCVForVacancy(
    vacancyId: string,
    options?: CVGenerationOptions
  ): Promise<{ cv: GeneratedCV; rendered: string; atsScore?: number }> {
    const { isAuthenticated } = useAuthStore.getState();

    try {
      // Build request body
      const requestBody: { vacancyId: string; options?: CVGenerationOptions; profileData?: any } = {
        vacancyId,
        options,
      };

      // Always include profileData as fallback — backend prefers DB profile
      // for authenticated users, but uses this if DB profile is empty/missing
      const { profile } = useProfileStore.getState();
      if (!isAuthenticated && !profile) {
        throw new Error('Profile not found. Please create a profile first.');
      }
      if (profile) {
        requestBody.profileData = profile;
      }

      // ALWAYS call server for AI generation
      const response = await apiClient.post<
        ApiResponse<{ cv: GeneratedCV; rendered: string; atsScore?: number }>
      >('/api/v1/cv/generate-for-vacancy', requestBody);

      const result = response.data.data;

      // Save to localStorage if not authenticated
      if (!isAuthenticated) {
        await localCVService.save(result.cv);
      }

      return result;
    } catch (error) {
      console.error('CV generation for vacancy failed:', error);
      throw error;
    }
  },

  /**
   * Get user's CVs
   */
  async listCVs(filters?: {
    profileId?: string;
    vacancyId?: string;
    limit?: number;
    offset?: number;
  }): Promise<GeneratedCV[]> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      return localCVService.list();
    }
    const params = new URLSearchParams();
    if (filters?.profileId) params.append('profileId', filters.profileId);
    if (filters?.vacancyId) params.append('vacancyId', filters.vacancyId);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get<ApiResponse<{ items: GeneratedCV[]; count: number }>>(`/api/v1/cv${query}`);
    const payload = response.data?.data;
    if (!payload) return [];
    // Backend returns { items, count } — extract the array
    return Array.isArray(payload) ? payload : (payload as any)?.items || [];
  },

  /**
   * Get CV by ID
   */
  async getCV(id: string): Promise<GeneratedCV> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      const cv = await localCVService.get(id);
      if (!cv) {
        throw new Error('CV not found in local mode');
      }
      return cv;
    }
    const response = await apiClient.get<ApiResponse<GeneratedCV>>(`/api/v1/cv/${id}`);
    return response.data.data;
  },

  /**
   * Delete CV
   */
  async deleteCV(id: string): Promise<void> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      await localCVService.remove(id);
      return;
    }
    await apiClient.delete(`/api/v1/cv/${id}`);
  },

  /**
   * Export CV to specified format
   * @param template Optional template to use for export (professional, modern, twoColumn, harvard)
   */
  async exportCV(
    id: string,
    format: ExportFormat,
    template?: 'professional' | 'modern' | 'twoColumn' | 'harvard'
  ): Promise<ExportResult> {
    const { mode } = useAuthStore.getState();
    if (mode === 'local') {
      const cv = await localCVService.get(id);
      if (!cv) {
        throw new Error('CV not found in local mode');
      }
      const blob = new Blob([cv.rendered], { type: 'text/html' });
      return { buffer: blob, filename: `cv.${format}`, mimeType: 'text/html' };
    }
    
    // Build query params
    const params = new URLSearchParams({ format });
    if (template) {
      params.append('template', template);
    }
    
    const response = await apiClient.get(`/api/v1/cv/${id}/export?${params.toString()}`, {
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `cv.${format}`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    return {
      buffer: response.data,
      filename,
      mimeType: response.headers['content-type'],
    };
  },

  /**
   * Get supported export formats
   */
  async getSupportedExportFormats(): Promise<ExportFormat[]> {
    const response = await apiClient.get<ApiResponse<{ formats: ExportFormat[] }>>(
      '/api/v1/cv/export-formats'
    );
    return response.data.data.formats;
  },

  /**
   * Download exported CV file
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Analyze ATS compatibility (supports local profile when unauthenticated)
   */
  async analyzeATS(): Promise<ATSAnalysisResult> {
    const { isAuthenticated, mode } = useAuthStore.getState();
    const { profile } = useProfileStore.getState();

    // Local/unauthenticated: send profileData explicitly
    if (!isAuthenticated || mode === 'local') {
      if (!profile) {
        throw new Error('Profile not found. Please create your profile first.');
      }
      const response = await apiClient.post<ApiResponse<ATSAnalysisResult>>(
        '/api/v1/cv/analyze-ats',
        { profileData: profile },
        { timeout: ATS_ANALYSIS_TIMEOUT_MS }
      );
      return response.data.data;
    }

    // Authenticated/cloud: use server-side profile
    const response = await apiClient.post<ApiResponse<ATSAnalysisResult>>(
      '/api/v1/cv/analyze-ats',
      undefined,
      { timeout: ATS_ANALYSIS_TIMEOUT_MS }
    );
    return response.data.data;
  },

  /**
   * Analyze ATS compatibility using V3 full-tier endpoint
   * Converts web-app profile to V3 input format and calls /api/v1/cv/analyze-ats/v3/full
   */
  async analyzeATSV3(): Promise<FullATSScoreV3> {
    const { profile } = useProfileStore.getState();

    if (!profile) {
      throw new Error('Profile not found. Please create your profile first.');
    }

    // Convert web-app profile to V3 analysis input format
    const data = {
      personalInfo: {
        fullName: `${profile.firstName} ${profile.lastName}`.trim(),
        email: profile.email || undefined,
        phone: profile.phone || undefined,
        location: profile.location || undefined,
      },
      summary: profile.summary || undefined,
      experience: profile.experience.map((exp) => ({
        company: exp.company,
        position: exp.position,
        startDate: exp.startDate,
        endDate: exp.endDate || 'Present',
        description: exp.description || undefined,
      })),
      education: profile.education.map((edu) => ({
        institution: edu.institution,
        degree: edu.degree,
        field: edu.fieldOfStudy || undefined,
        startDate: edu.startDate,
        endDate: edu.endDate || undefined,
      })),
      skills: {
        technical: profile.skills,
        languages: profile.languages,
      },
      certifications: profile.certifications.map((cert) => ({
        name: cert.name,
        issuer: cert.issuer,
        date: cert.issueDate,
        expiryDate: cert.expiryDate || undefined,
      })),
    };

    const targetRole = profile.experience[0]?.position || undefined;

    try {
      const response = await apiClient.post<ApiResponse<FullATSScoreV3>>(
        '/api/v1/cv/analyze-ats/v3/full',
        { data, targetRole },
        { timeout: ATS_ANALYSIS_TIMEOUT_MS }
      );
      return response.data.data;
    } catch (error: any) {
      // Fallback to V2 endpoint if V3 not deployed yet
      if (error?.response?.status === 404) {
        console.log('[cvService] V3 endpoint not found, falling back to V2...');
        const v2Result = await this.analyzeATS();
        return v2ToV3(v2Result as any);
      }
      throw error;
    }
  },
};
